import React, { useRef, useState } from "react";
import {
  View,
  TouchableWithoutFeedback,
  TextInput,
  StyleSheet,
  Pressable,
  TouchableOpacity,
} from "react-native";
import colors from "@constants/color";
import Text from "@components/CustomText";
import SearchIcon from "@assets/svg/SearchIcon";
import Cross from "@assets/svg/Cross";
import MapMarker from "@assets/svg/MapMarker";
import analytics from "@services/analytics";
import { MAP_VIEW_EVENTS } from "@constants/analyticsEventsCT";
import BackButton from "../BackButton";
import { fonts } from "../../constants/metrics";

function HeaderWithSearch({
  headertitle = "",
  headerStyle,
  navigation,
  route,
  searchIcon,
  hideShadow = false,
  onPressCrossIcon = () => {},
  onPressSearchIcon = () => {},
  goingBackHandler,
  alwaysEnable = true,
  numberOfLines = 0,
  testID = "",
  isSearchEnabled = false,
  isMapEnabled = false,
  mapScreen = "OrdersMapView",
  onSearchInputChange = () => {},
  searchPlaceholder = "",
  rightComponent = <View />,
}) {
  const [searchText, setSearchText] = useState("");
  const textInputRef = useRef(null);

  return (
    <View style={styles.container}>
      <View
        style={[styles.headerContainer, hideShadow ? { elevation: 0 } : null]}
      >
        <BackButton
          onPress={goingBackHandler ?? navigation.goBack}
          alwaysEnable={alwaysEnable}
        />
        <View style={styles.headerTitle}>
          <Text
            variant="title_18"
            numberOfLines={numberOfLines}
            style={[styles.titleText, headerStyle || null]}
          >
            {headertitle}
          </Text>
        </View>
        <View style={styles.rightComponentContainer}>
          <View style={styles.rightComponent}>{rightComponent}</View>
          {searchIcon && !isSearchEnabled && (
            <View style={styles.searchIconContainer}>
              <TouchableWithoutFeedback onPress={onPressSearchIcon}>
                <View style={styles.searchIcon}>{searchIcon}</View>
              </TouchableWithoutFeedback>
            </View>
          )}
        </View>
        {isMapEnabled ? (
          <TouchableOpacity
            onPress={() => {
              navigation.navigate(mapScreen);
              analytics.trackEvent(MAP_VIEW_EVENTS.MAP_BTN_PRESS);
            }}
            style={{
              backgroundColor: "rgba(51, 51, 51, 1)",
              flexDirection: "row",
              alignItems: "center",
              marginRight: 10,
              paddingHorizontal: 10,
              paddingVertical: 7,
              borderRadius: 50,
            }}
          >
            <MapMarker width={17} height={17} />
            <Text style={{ color: "white" }}> Map</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      {isSearchEnabled && (
        <SearchField
          searchPlaceholder={searchPlaceholder}
          onSearchInputChange={onSearchInputChange}
          searchText={searchText}
          onPressCrossIcon={onPressCrossIcon}
          textInputRef={textInputRef}
          testID={testID}
          setSearchText={setSearchText}
          hideShadow={hideShadow}
        />
      )}
    </View>
  );
}

function SearchField({
  searchPlaceholder,
  onSearchInputChange,
  searchText,
  onPressCrossIcon,
  textInputRef,
  testID = "",
  setSearchText = () => {},
  hideShadow = false,
}) {
  return (
    <View style={[styles.headerContainer, { elevation: 0 }]}>
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <SearchIcon height={20} width={20} />
          <TextInput
            ref={textInputRef}
            testID={testID}
            placeholder={searchPlaceholder}
            autoFocus={true}
            placeholderTextColor={colors.mediumBlacklightBlack}
            cursorColor={colors.mediumBlacklightBlack}
            returnKeyType="done"
            style={styles.searchInput}
            onChangeText={(text) => {
              setSearchText(text);
              onSearchInputChange(text);
            }}
          />
        </View>

        {searchText.length > 0 ? (
          <Pressable
            onPress={() => {
              textInputRef.current.clear();
              onPressCrossIcon();
              setSearchText("");
            }}
            testID="searchIcon"
            style={styles.crossIconContainer}
          >
            <Cross height={15} width={15} />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
  },
  headerContainer: {
    minHeight: 66,
    backgroundColor: colors.white,
    shadowColor: "rgba(0, 0, 0, 1)",
    shadowOffset: {
      width: 0,
      height: 3,
    },
    shadowRadius: 5,
    shadowOpacity: 0.8,
    elevation: 3,
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },

  headerTitle: {
    marginLeft: 4,
    flex: 1,
  },
  titleText: {
    fontSize: fonts.content1,
  },
  rightComponentContainer: {
    flexDirection: "row",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  rightComponent: {
    right: 0,
    minWidth: 40,
    alignItems: "center",
    marginRight: 12,
  },
  searchIconContainer: {
    right: 0,
    minWidth: 40,
    alignItems: "center",
  },
  searchIcon: {
    paddingTop: 5,
    paddingLeft: 5,
    paddingRight: 20,
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 15,
    marginBottom: 12,
    paddingLeft: 12,
    borderRadius: 50,
    elevation: 5,
    backgroundColor: "white",
    borderColor: "rgba(229, 229, 229, 1)",
    borderWidth: 0.5,
    justifyContent: "space-between",
    paddingRight: 12,
    height: 50,
  },
  searchInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  searchInput: {
    width: "85%",
    backgroundColor: colors.white,
    color: colors.black,
    fontSize: 14,
    fontWeight: "500",
  },
  crossIconContainer: {
    padding: 5,
  },
});

export default HeaderWithSearch;
